import React, { useEffect } from 'react';

// --- CSS Styles Component ---
// To resolve the import error, all CSS styles are embedded directly within the component.
// This makes the component self-contained and ensures it runs correctly in any environment.
const ObjectivesStyles = () => (
    <style>{`
        /* --- Root Variables for easy theme management --- */
        :root {
            --primary-color-obj: #ff8800; /* Vibrant Orange */
            --secondary-color-obj: #ffc107; /* Amber/Yellow */
            --background-color-obj: #fffaf0; /* Light Creamy Background */
            --card-background-obj: #ffffff;
            --text-color-obj: #2c3e50; /* Darker Slate Blue */
            --subtle-text-obj: #7f8c8d; /* Grayish Cyan */
            --border-color-obj: #f0e6d2;
            --shadow-color-obj: rgba(0, 0, 0, 0.08);
        }

        /* --- General Section Styling --- */
        .objectives-section {
            background-color: var(--background-color-obj);
            padding: 4rem 1rem; /* Adjusted padding for mobile */
            font-family: 'Poppins', sans-serif;
        }

        .objectives-container {
            max-width: 1200px;
            margin: 0 auto;
        }

        /* --- Premium Heading Styling --- */
        .heading-wrapper-objectives {
            text-align: center;
            margin-bottom: 3rem;
        }

        .heading-wrapper-objectives h2 {
            font-size: 2.5rem;
            font-weight: 700;
            color: var(--text-color-obj);
            margin-bottom: 0.5rem;
            position: relative;
            display: inline-block;
        }

        .header-underline-objectives {
            width: 70px;
            height: 4px;
            background: linear-gradient(90deg, var(--primary-color-obj), var(--secondary-color-obj));
            margin: 0.5rem auto 0 auto;
            border-radius: 2.5px;
        }

        /* --- Grid Layout for Objectives --- */
        .objectives-grid {
            display: grid;
            grid-template-columns: 1fr; /* Single column on small screens */
            gap: 1.5rem;
        }

        /* --- Individual Objective Card Styling --- */
        .objective-card {
            background-color: var(--card-background-obj);
            border-radius: 16px;
            padding: 2rem;
            display: flex;
            align-items: flex-start;
            gap: 1.5rem;
            border: 1px solid var(--border-color-obj);
            box-shadow: 0 4px 15px var(--shadow-color-obj);
            transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
            
            /* Animation properties */
            opacity: 0;
            transform: translateY(30px);
            animation: cardFadeUp 0.6s ease-out forwards;
            animation-delay: var(--delay);
        }

        @keyframes cardFadeUp {
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }

        .objective-card:hover {
            transform: translateY(-8px);
            box-shadow: 0 12px 25px rgba(0, 0, 0, 0.1);
            border-color: var(--primary-color-obj);
        }

        /* --- Icon Container Styling --- */
        .objective-icon-container {
            flex-shrink: 0;
            width: 50px;
            height: 50px;
            display: flex;
            justify-content: center;
            align-items: center;
            border-radius: 50%;
            background: linear-gradient(135deg, var(--primary-color-obj), var(--secondary-color-obj));
            color: white;
            box-shadow: 0 4px 10px rgba(255, 136, 0, 0.4);
            transition: transform 0.3s ease;
        }

        .objective-card:hover .objective-icon-container {
            transform: scale(1.1) rotate(15deg);
        }

        .objective-icon-container svg {
            width: 25px;
            height: 25px;
        }

        /* --- Content Styling within the Card --- */
        .objective-content h3 {
            font-size: 1.25rem;
            font-weight: 600;
            color: var(--text-color-obj);
            margin: 0 0 0.5rem 0;
        }

        .objective-content p {
            font-size: 0.95rem;
            line-height: 1.6;
            color: var(--subtle-text-obj);
            margin: 0;
        }

        /* --- Responsive Design --- */
        
        /* Tablet View */
        @media (min-width: 768px) {
            .objectives-section {
                padding: 5rem 2rem;
            }
            .heading-wrapper-objectives h2 {
                font-size: 2.8rem;
            }
            .objectives-grid {
                grid-template-columns: repeat(2, 1fr);
                gap: 2rem;
            }
            .objective-card {
                padding: 2.5rem;
            }
            .objective-icon-container {
                width: 60px;
                height: 60px;
            }
            .objective-icon-container svg {
                width: 30px;
                height: 30px;
            }
             .objective-content h3 {
                font-size: 1.4rem;
            }
            .objective-content p {
                font-size: 1rem;
            }
        }

        /* Desktop but small Screens */
        @media (min-width: 1024px) {
            .objectives-grid {
                /* Two columns for a more compact and premium look on large screens */
                grid-template-columns: repeat(2, 1fr);
            }
        }

        /* Desktop and Large Screens */
        @media (min-width: 1280px) {
            .objectives-grid {
                /* Two columns for a more compact and premium look on large screens */
                grid-template-columns: repeat(2, 1fr);
            }
        }
    `}</style>
);


// --- SVG Icon Components ---
// Using inline SVGs for icons to keep the component self-contained and performant.
const GoodnessIcon = () => <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M50 95C74.8528 95 95 74.8528 95 50C95 25.1472 74.8528 5 50 5C25.1472 5 5 25.1472 5 50C5 74.8528 25.1472 95 50 95Z" stroke="currentColor" strokeWidth="5"/><path d="M62.5 50C62.5 56.9036 56.9036 62.5 50 62.5C43.0964 62.5 37.5 56.9036 37.5 50C37.5 43.0964 43.0964 37.5 50 37.5C56.9036 37.5 62.5 43.0964 62.5 50Z" stroke="currentColor" strokeWidth="5"/><path d="M50 5V95" stroke="currentColor" strokeWidth="5"/><path d="M95 50H5" stroke="currentColor" strokeWidth="5"/></svg>;
const LifestyleIcon = () => <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M50 5L95 50L50 95L5 50L50 5Z" stroke="currentColor" strokeWidth="5"/><path d="M50 27.5L72.5 50L50 72.5L27.5 50L50 27.5Z" stroke="currentColor" strokeWidth="5"/></svg>;
const AssociationIcon = () => <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="50" cy="50" r="45" stroke="currentColor" strokeWidth="5"/><path d="M50 5V95" stroke="currentColor" strokeWidth="5"/><path d="M95 50H5" stroke="currentColor" strokeWidth="5"/><path d="M78.2843 21.7157L21.7157 78.2843" stroke="currentColor" strokeWidth="5"/><path d="M78.2843 78.2843L21.7157 21.7157" stroke="currentColor" strokeWidth="5"/></svg>;
const SupportIcon = () => <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M50 95V5L5 50H95L50 95Z" stroke="currentColor" strokeWidth="5"/></svg>;
const MentorshipIcon = () => <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M50 5L95 95H5L50 5Z" stroke="currentColor" strokeWidth="5"/><path d="M50 50L72.5 95H27.5L50 50Z" stroke="currentColor" strokeWidth="5"/></svg>;
const CommunityIcon = () => <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 5H50V50H5V5Z" stroke="currentColor" strokeWidth="5"/><path d="M50 5H95V50H50V5Z" stroke="currentColor" strokeWidth="5"/><path d="M5 50H50V95H5V50Z" stroke="currentColor" strokeWidth="5"/><path d="M50 50H95V95H50V50Z" stroke="currentColor" strokeWidth="5"/></svg>;
const CultureIcon = () => <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M50 5C25.1472 5 5 25.1472 5 50C5 74.8528 25.1472 95 50 95V5Z" stroke="currentColor" strokeWidth="5"/><path d="M50 5C74.8528 5 95 25.1472 95 50C95 74.8528 74.8528 95 50 95V5Z" stroke="currentColor" strokeWidth="5" strokeDasharray="10 5"/></svg>;
const ResponsibilityIcon = () => <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 50C5 25.1472 25.1472 5 50 5C74.8528 5 95 25.1472 95 50V95H5V50Z" stroke="currentColor" strokeWidth="5"/></svg>;
const EquilibriumIcon = () => <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 50H95" stroke="currentColor" strokeWidth="5"/><path d="M50 5L25 95" stroke="currentColor" strokeWidth="5"/><path d="M50 5L75 95" stroke="currentColor" strokeWidth="5"/></svg>;
const LeadershipIcon = () => <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="50" cy="25" r="20" stroke="currentColor" strokeWidth="5"/><path d="M50 45V95" stroke="currentColor" strokeWidth="5"/><path d="M5 95H95" stroke="currentColor" strokeWidth="5"/></svg>;


// --- Data for Objectives ---
// Content has been summarized for a more concise and premium feel.
const objectivesData = [
    {
        icon: <GoodnessIcon />,
        title: "Platform for Expressing Goodness",
        description: "To provide a platform that encourages and inspires the expression of inherent goodness, often neglected due to a lack of opportunity."
    },
    {
        icon: <LifestyleIcon />,
        title: "Promote Value Centered Lifestyle",
        description: "To promote a lifestyle centered on core values, recognizing them as essential jewels that give meaning and transform one's personality."
    },
    {
        icon: <AssociationIcon />,
        title: "Association",
        description: "To foster meaningful associations that shape character through knowledge sharing and thoughtful discussions, leading to personal transformation."
    },
    {
        icon: <SupportIcon />,
        title: "Support System",
        description: "To create a supportive and favorable environment that acts as a driving force for individuals on their path to success."
    },
    {
        icon: <MentorshipIcon />,
        title: "Personalized Mentorship",
        description: "To offer personalized guidance and mentorship, helping individuals navigate their unique goals and overcome personal obstacles."
    },
    {
        icon: <CommunityIcon />,
        title: "Community Building",
        description: "To build a strong community by encouraging teamwork over individualism through regular, value-based activities for a common cause."
    },
    {
        icon: <CultureIcon />,
        title: "Preserving Culture",
        description: "To preserve and promote culture by creating an atmosphere that facilitates a natural and standard system of value-based education."
    },
    {
        icon: <ResponsibilityIcon />,
        title: "Sense of Responsibility",
        description: "To train and educate youth to become responsible citizens, who are essential assets for the nation's holistic development."
    },
    {
        icon: <EquilibriumIcon />,
        title: "Providing Life Equilibrium",
        description: "To help individuals achieve a balanced life by harmonizing their Physical, Intellectual, Emotional, and Spiritual Quotients."
    },
    {
        icon: <LeadershipIcon />,
        title: "Role Model and Inspirational Leadership",
        description: "To develop exemplary youth leaders who can inspire society, pioneer new frontiers, and create new opportunities for growth."
    }
];

// --- Main Objectives Component ---
const Objectives = () => {

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('visible');
                }
            });
        }, {
            threshold: 0.1
        });

        const cards = document.querySelectorAll('.objective-card');
        cards.forEach(card => observer.observe(card));

        // Cleanup observer on component unmount
        return () => cards.forEach(card => observer.unobserve(card));
    }, []);

    return (
        <>
            <ObjectivesStyles />
            <section className="objectives-section">
                <div className="objectives-container">
                    <div className="heading-wrapper-objectives">
                        <h2>Our Objectives</h2>
                        <div className="header-underline-objectives"></div>
                    </div>

                    <div className="objectives-grid">
                        {objectivesData.map((objective, index) => (
                            <div className="objective-card" key={index} style={{ '--delay': `${index * 100}ms` }}>
                                <div className="objective-icon-container">
                                    {objective.icon}
                                </div>
                                <div className="objective-content">
                                    <h3>{objective.title}</h3>
                                    <p>{objective.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
};

export default Objectives;